import React, { useCallback } from 'react';
import { Box } from '@radix-ui/themes';
import { RenderFields } from './RenderFields';
import { ApiAttribute } from '@context/openapi/types';
import { isFieldVisibilityOverridden } from '@/services/util';
import { useExplorer } from '@/hooks/useExplorer';

export enum ApiAttributeType {
  QUERY,
  PATH,
  BODY,
}

export interface DynamicFormProps {
  options?: IDynamicFormOptions;
  className?: string;
  updateParams: (params: ApiAttribute[], type: ApiAttributeType) => void;
}

export interface IDynamicFormOptions {
  params: ApiAttribute[];
  type: ApiAttributeType;
  disable?: boolean;
  isEmptyAttributesVisible: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  options,
  updateParams,
}) => {
  /* Update field value based on key hierarchy - Input Field
      If the field key contains array index, update the array field value
      Else update the field value
  */
  const { visibleFieldsOverride } = useExplorer();
  const onChange = useCallback(
    (field: ApiAttribute, value: string) => {
      const updateFieldValue = (
        fields: ApiAttribute[],
        fieldKey: string,
        value: string
      ) => {
        fields.forEach((f) => {
          if (f.key === fieldKey) {
            f.value = value;
          }
          if (f.child) {
            updateFieldValue(f.child, fieldKey, value);
          }
        });
      };

      updateFieldValue(options?.params ?? [], field.key, value);
      updateParams(
        options?.params ?? [],
        options?.type ?? ApiAttributeType.BODY
      );
    },
    [options?.params, options?.type, updateParams]
  );

  const onFilterChange = useCallback(
    (field: ApiAttribute, value: string) => {
      const updateFilterValue = (
        fields: ApiAttribute[],
        fieldKey: string,
        value: string
      ) => {
        fields.forEach((f) => {
          if (f.key === fieldKey) {
            f.filter = value;
            // Compute and set filterValueType based on the filter operator
            // Array operators: in, not_in, between
            f.filterValueType = ['in', 'not_in', 'between'].includes(
              value.toLowerCase()
            )
              ? 'array'
              : 'single';
          }
          if (f.child) {
            updateFilterValue(f.child, fieldKey, value);
          }
        });
      };

      updateFilterValue(options?.params ?? [], field.key, value);
      updateParams(
        options?.params ?? [],
        options?.type ?? ApiAttributeType.QUERY
      );
    },
    [options?.params, options?.type, updateParams]
  );

  const onUpdateItem = (field: ApiAttribute) => {
    const updatedFields = options?.params.map((f) => {
      if (f.key === field.key) {
        return field;
      }
      return f;
    });
    if (updatedFields)
      updateParams(updatedFields, options?.type ?? ApiAttributeType.BODY);
  };

  return (
    <div className="[&>*:first-child]:border-t [&>*:first-child]:border-solid [&>*:first-child]:border-form-border">
      {options?.params.map((field) => (
        <Box key={field.key}>
          {(isFieldVisibilityOverridden(field, visibleFieldsOverride) ||
            options.isEmptyAttributesVisible) && (
            <RenderFields
              field={field}
              onChange={onChange}
              onFilterChange={onFilterChange}
              onUpdateItem={onUpdateItem}
              isEmptyAttributesVisible={options.isEmptyAttributesVisible}
            />
          )}
        </Box>
      ))}
    </div>
  );
};

export default DynamicForm;
